import {
  Box,
  Button,
  Flex,
  Switch,
  Text as ChakraText,
  Link,
} from '@chakra-ui/react'
import React, { useContext } from 'react'
import { Alert, AlertIcon, AlertDescription } from '@chakra-ui/alert'
import { useTranslation, Trans } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import type { FormikHelpers } from 'formik'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import type { GlobalMultiFactorAuthentication } from '../../interfaces/MultiFactorAuthentication'
import { UserContext } from '../../providers'
import { MainContentHeadingBox } from '../Base/MainContent'
import useToast from '../../hooks/useToast'
import { SettingsToggleBox } from '../../components/Settings'

const MultiFactorAuthenticationLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user, refetch: refetchUser } = useContext(UserContext)
  const toast = useToast()
  const { request } = useFetch<GlobalMultiFactorAuthentication>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const handleSubmit = async (
    values: GlobalMultiFactorAuthentication,
    _formikHelpers: FormikHelpers<GlobalMultiFactorAuthentication>,
  ): Promise<void> => {
    if (user?.clientNumber) {
      await request.post(
        `/api/clients/${user.clientNumber}/multi-factor-auth`,
        values,
      )
      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      toast({
        description: t(
          'successfully_updated_multifactor_authentication',
          'Successfully updated multi-factor authentication',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      await refetchUser()
    }
  }

  const form = useFormik<GlobalMultiFactorAuthentication>({
    initialValues: {
      globalMultiFactorAuth: user?.globalMultiFactorAuth ?? false,
    },
    validationSchema: Yup.object().shape({
      globalMultiFactorAuth: Yup.boolean().required(t('required', 'Required')),
    }),
    onSubmit: handleSubmit,
  })

  const { submitForm, isSubmitting, handleChange, isValid, values } = form

  return (
    <>
      <MainContentHeadingBox reverse={true}>
        <Button
          onClick={() => {
            void submitForm()
          }}
          isLoading={isSubmitting}
          isDisabled={
            !isValid ||
            user?.globalMultiFactorAuth === values.globalMultiFactorAuth
          }
        >
          {t('save')}
        </Button>
      </MainContentHeadingBox>
      <Box px={12} py={4}>
        <SettingsToggleBox>
          <Flex justifyContent="space-between" width="100%">
            <ChakraText fontWeight="700">
              {t(
                'global_multifactor_authentication',
                'Global multi-factor authentication',
              )}
            </ChakraText>
            <Switch
              name="globalMultiFactorAuth"
              isChecked={values.globalMultiFactorAuth}
              onChange={handleChange}
            />
          </Flex>
          <ChakraText
            color="appBattleshipGrey"
            paddingRight={8}
            marginBottom={2}
          >
            {t(
              'multifactor_authentication_description',
              `Multi-factor authentication adds a layer of protection to the log-in process.\n
             A one time password is generated and sent to the user's registered email address 
             which is used for additonal identity verification.`,
            )}
          </ChakraText>
          <ChakraText>
            {t('we_recommend')} :&nbsp;
            <ChakraText as="span" color="appGreenishTeal">
              ON
            </ChakraText>
          </ChakraText>
          <Alert status="info">
            <AlertIcon height="1em" width="1em" />
            <AlertDescription fontSize="sm">
              <ChakraText mt={1}>
                {t(
                  'enforce_MFA_desc',
                  'Allowing this setting will enforce MFA for all user accounts on this ImageKit account.',
                )}
              </ChakraText>
            </AlertDescription>
          </Alert>
          <Alert status="info">
            <AlertIcon
              height="1em"
              width="1em"
              alignItems="flex-start"
              marginTop="4px"
            />
            <AlertDescription fontSize="sm">
              <Trans t={t} i18nKey="MFA_SSO_desc">
                This setting is not applicable for users logging in via single
                sign-on (SSO) authentication. If you wish to enable MFA, you may
                do so in your Identity Provider (IdP) platform.{' '}
                <Link
                  fontSize="1em"
                  href="https://docs.imagekit.io/features/single-sign-on"
                  isExternal
                >
                  Learn more
                </Link>
                .
              </Trans>
            </AlertDescription>
          </Alert>
        </SettingsToggleBox>
      </Box>
    </>
  )
}

export default MultiFactorAuthenticationLayout
